/**
 * Block Color Palette
 *
 * Consistent colors for visualization blocks.
 * Uses CSS variables where possible for theme support.
 */

/**
 * Chart series colors — used for line charts, bar charts, etc.
 */
export const CHART_COLORS = [
  'var(--color-primary)',       // Blue - primary metric
  'var(--color-danger)',        // Red - alerts, decreases
  'var(--color-success)',       // Green - positive, increases
  'var(--color-warning)',       // Orange - warnings, attention
  'var(--color-accent-strong)', // Purple - secondary emphasis
  'var(--color-accent)',        // Teal - tertiary
  'var(--color-accent-bright)', // Pink - highlight
  '#84cc16',                    // Lime - additional
] as const;

/**
 * Semantic colors for trends and status
 */
export const TREND_COLORS = {
  positive: 'var(--color-success)',
  negative: 'var(--color-danger)',
  neutral: 'var(--color-text-muted)',
} as const;

/**
 * Background colors for cards and containers
 */
export const SURFACE_COLORS = {
  base: 'var(--color-surface)',
  muted: 'var(--color-surface-muted)',
  hover: 'var(--color-surface-hover)',
} as const;

/**
 * Border colors
 */
export const BORDER_COLORS = {
  default: 'var(--color-border)',
  strong: 'var(--color-border-strong)',
} as const;

/**
 * Text colors
 */
export const TEXT_COLORS = {
  primary: 'var(--color-text)',
  subtle: 'var(--color-text-subtle)',
  muted: 'var(--color-text-muted)',
  faint: 'var(--color-text-faint)',
} as const;

/**
 * Heatmap intensity colors (GitHub-style)
 */
export const HEATMAP_COLORS = [
  'var(--color-surface-muted)',  // Level 0 - no activity
  '#9be9a8',                     // Level 1 - low
  '#40c463',                     // Level 2 - medium
  '#30a14e',                     // Level 3 - high
  '#216e39',                     // Level 4 - very high
] as const;

/**
 * Get color for a series index
 */
export function getSeriesColor(index: number): string {
  return CHART_COLORS[index % CHART_COLORS.length];
}

/**
 * Get trend color based on value
 */
export function getTrendColor(trend: number): string {
  if (trend > 0) return TREND_COLORS.positive;
  if (trend < 0) return TREND_COLORS.negative;
  return TREND_COLORS.neutral;
}

/**
 * Get heatmap color for intensity level (0-4)
 */
export function getHeatmapColor(intensity: number): string {
  const level = Math.min(4, Math.max(0, Math.round(intensity)));
  return HEATMAP_COLORS[level];
}
